import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import clsx from "clsx";
import { useCopilotContext } from "@copilotkit/react-core";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import { ForecastNewProductContentProps } from "./types/ForecastNewProductStepForm";
import Button from "components/common/Button";
import TextInput from "components/common/InputField/TextInput";
import SelectInput from "components/common/InputField/SelectInput";
import { FORECAST_NEW_PRODUCT_STEP } from "./constant/ForecastNewProductStepConstant";
import CustomTable from "components/common/CustomTable";
import TableBodyItem from "components/common/TableBodyItem";
import {
    useDeleteNewProductPlan,
    useGetNewProductPlans,
    usePinNewProductPlan,
    useUnpinNewProductPlan,
} from "hooks/newProductPlanHooks";
import Dropdown from "components/common/Dropdown";
import { NewProductPlanStatusEnum } from "enums/NewProductPlanStatusEnum";
import { FORECAST_NEW_PRODUCT_DATE_RANGES } from "./constant/ForecastNewProductDateRangeConstant";
import CustomToast from "components/common/CustomToast";
import Loader from "components/common/Loader";

type ForecastNewProductPlanListProps = {} & ForecastNewProductContentProps &
    Omit<ModalContainerProps, "children">;

export default function ForecastNewProductPlanList(props: ForecastNewProductPlanListProps) {
    const { stepper, formik, tailwindClassName, ...rest } = props;
    const [deleteNewProductPlan, deleteNewProductPlanResult] = useDeleteNewProductPlan();
    const { setThreadId } = useCopilotContext();
    const [_, setSearchParams] = useSearchParams();

    const scrollContentId = "new-product-list-container";

    const [filter, setFilter] = useState({
        forecastPeriod: "1_MONTH",
        search: "",
    });

    const getNewProductPlanListQuery = useGetNewProductPlans(
        useMemo(
            () => ({
                ...(filter?.search ? { search: filter?.search } : {}),
                ...(filter?.forecastPeriod ? { forecastPeriod: filter?.forecastPeriod } : {}),
            }),
            [filter?.search, filter?.forecastPeriod]
        ),
        {
            isInfiniteScroll: true,
            scrollContentId,
        }
    );

    const handleDelete = async (productPlanId: string) => {
        try {
            await deleteNewProductPlan(productPlanId);
            await getNewProductPlanListQuery.refetch();
            toast.custom((toastRef) => (
                <CustomToast
                    t={toastRef}
                    message={"Product plan deleted successfully"}
                    type="success"
                />
            ));
        } catch (error) {
            toast.custom((toastRef) => (
                <CustomToast
                    t={toastRef}
                    message={error?.message || "Something went wrong"}
                    type="error"
                />
            ));
        }
    };

    const {
        pinNewProductPlan,
        isLoading: isPinning,
        isSuccess: isPinnedSuccess,
    } = usePinNewProductPlan();
    const {
        unpinNewProductPlan,
        isLoading: isUnpinning,
        isSuccess: isUnpinnedSuccess,
    } = useUnpinNewProductPlan();

    const handlePin = (productPlanId: string) => {
        pinNewProductPlan(productPlanId);
    };

    const handleUnpin = (productPlanId: string) => {
        unpinNewProductPlan(productPlanId);
    };

    const tableHeader = [
        {
            title: "",
            widthClass: "w-[50px]",
        },
        {
            title: "Product",
            widthClass: "w-[20%]",
        },
        {
            title: "Launch date",
        },
        {
            title: "Associated product(s)",
            widthClass: "w-[20%]",
        },
        {
            title: "Projected growth rate",
        },
        {
            title: "Projected sales",
        },
        {
            title: "Created by",
        },
        {
            title: "",
            widthClass: "w-[50px]",
        },
    ];

    const tableBody = [
        ...getNewProductPlanListQuery?.data?.map((product) => {
            return {
                _id: product?._id,
                product: product?.productDetails?.[0]?.productName || "N/A",
                productId:
                    `${product?.productDetails?.[0]?._id}-${product?.productDetails?.[0]?.productName}` ||
                    "N/A",
                sku: product?.productDetails?.[0]?.sku || "N/A",
                launchDate: moment(product?.launchDate).format("YYYY-DD-MM"),
                associatedProduct: product?.similarProducts?.map(
                    (item) => item?.product?.productName
                ),
                status: product?.status,
                projectedGrowthRate: product?.growthRate || "N/A",
                projectedSales: product?.projectedSales || "N/A",
                createdBy: product?.createdByDetails?.fullName || "N/A",
                createdAt: moment(product?.createdAt).format("YYYY-DD-MM"),
                isPinned: product?.isPinned,
                ldbId: product?.ldbId,
                threadId: product?.threadId,
                channel: product?.productChannel,
            };
        }),
    ].map((item) => {
        const isPinned = item?.isPinned;

        return [
            {
                content: (
                    <TableBodyItem className="flex flex-row justify-center !px-0 !items-center">
                        <button disabled={isPinning || isUnpinning}>
                            <i
                                className={`ri-star-fill text-center text-[20px] cursor-pointer ${
                                    isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0]"
                                }`}
                                title={isPinned ? "Unpin" : "Pin"}
                                onClick={() => {
                                    if (isPinned) {
                                        handleUnpin(item?._id);
                                    } else {
                                        handlePin(item?._id);
                                    }
                                }}
                            ></i>
                        </button>
                    </TableBodyItem>
                ),
            },
            {
                content: (
                    <TableBodyItem>
                        <p className="text-slate-400">{item?.sku}</p>
                        <p className="text-slate-700">{item?.product}</p>
                    </TableBodyItem>
                ),
                widthClass: "!w-[10%]",
            },
            {
                content: <TableBodyItem>{item?.launchDate}</TableBodyItem>,
            },
            {
                content: <TableBodyItem>{item?.associatedProduct?.join(", ")}</TableBodyItem>,
            },
            {
                content: <TableBodyItem>{item?.projectedGrowthRate || 0}%</TableBodyItem>,
            },
            {
                content: (
                    <TableBodyItem>{Math.round(Number(item?.projectedSales) || 0)}</TableBodyItem>
                ),
            },
            {
                content: <TableBodyItem>{item?.createdBy}</TableBodyItem>,
            },

            {
                content: (
                    <TableBodyItem className="flex justify-center items-center">
                        <Dropdown
                            dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                            dropdownContainerClasses={`shadow-cardShadow-2 z-[1400] border-0 rounded !max-h-fit right-[0px] right-[20px]`}
                            name={"dropdown"}
                            disabled={deleteNewProductPlanResult?.isLoading}
                            dropdownItemsClasses="!bg-transparent "
                            dropdownOptions={[
                                {
                                    label: "View",
                                    value: "View",
                                    action: () => {
                                        formik.setValues((prev) => ({
                                            ...prev,
                                            newProductPlanId: item?._id,
                                            threadId: item?.threadId,
                                            productId: item?.productId,
                                            productChannel: item?.channel,
                                        }));
                                        setThreadId(item?.threadId);

                                        if (item?.status === NewProductPlanStatusEnum.InProgress) {
                                            stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN_LOADER);
                                        } else {
                                            stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN_RESULT);
                                        }
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                ...(item?.status === NewProductPlanStatusEnum.completed
                                    ? [
                                          {
                                              label: "Edit",
                                              value: "Edit",
                                              action: () => {
                                                  formik.setFieldValue("ldbId", item?.ldbId);
                                                  formik.setFieldValue(
                                                      "newProductPlanId",
                                                      item?._id
                                                  );
                                                  formik.setFieldValue("threadId", item?.threadId);
                                                  setSearchParams({ newProductPlanId: item?._id });
                                                  stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN_EDIT);
                                              },
                                              labelClassName: "!text-slate-600 !text-sm",
                                          },
                                      ]
                                    : []),

                                {
                                    label: "Delete",
                                    value: "Delete",
                                    action: () => {
                                        handleDelete(item?._id);
                                    },
                                    labelClassName: "!text-red-500 !text-sm",
                                },
                            ]}
                        />
                    </TableBodyItem>
                ),
                widthClass: "!w-[50px]",
            },
        ];
    });

    useEffect(() => {
        if (isPinnedSuccess || isUnpinnedSuccess) {
            getNewProductPlanListQuery.refetch();
        }
    }, [isPinnedSuccess, isUnpinnedSuccess]);

    return (
        <ModalContainer {...rest} tailwindClassName={clsx("max-w-[1078px]", tailwindClassName)}>
            <div>
                <div className="flex justify-between items-center px-[24px] py-[16px]">
                    <button onClick={stepper.previous}>
                        <i className="ri-arrow-left-line"></i>
                    </button>

                    <p className="text-[18px]  font-rocGroteskMedium">All planned products</p>

                    <button onClick={rest.closeModal}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>

                <hr className="border-[#E2E8F0]" />

                <div className="p-[24px] space-y-4">
                    <div className="flex gap-2 items-center">
                        <div className="w-full">
                            <TextInput
                                type="text"
                                containerClassname="!bg-[#F8FAFC] !border-none"
                                widthClass="!w-full"
                                inputClassName="!w-full"
                                placeholder="Search planned products"
                                value={filter?.search}
                                onChange={(e) => {
                                    setFilter((prev) => ({
                                        ...prev,
                                        search: e.target.value,
                                    }));
                                }}
                            />
                        </div>

                        <div className="w-fit">
                            <SelectInput
                                dropdownOptions={Object.entries(
                                    FORECAST_NEW_PRODUCT_DATE_RANGES
                                ).map(([key, value]) => ({
                                    label: (
                                        <div className="flex flex-row items-center gap-2 capitalize">
                                            {key

                                                ?.split("_")
                                                .join(" ")
                                                ?.toLocaleLowerCase()
                                                ?.replace("1 month", "This Month")}
                                        </div>
                                    ),
                                    value: key,
                                }))}
                                value={filter?.forecastPeriod}
                                handleChange={(_, value) => {
                                    setFilter((prev) => ({
                                        ...prev,
                                        forecastPeriod: value,
                                    }));
                                }}
                            />
                        </div>

                        <Button
                            btnText="Forecast demand for a new product"
                            btnClassname="!w-fit !whitespace-nowrap"
                            onClick={() => {
                                stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN);
                                setSearchParams({});
                                formik.resetForm();
                            }}
                        />
                    </div>
                    <div className="relative mb-6">
                        <CustomTable
                            tableBody={tableBody}
                            tableHeader={tableHeader}
                            isCellBordered
                            // isInifiniteScroll
                            isScrollable
                            scrollContentId={scrollContentId}
                            scrollWithin={true}
                            isCellSelectable={false}
                            isAllSelectable={false}
                            isLoading={getNewProductPlanListQuery?.isLoading}
                            // tableContainerClass="!max-h-[427px]"
                            headerContainerClass="!bg-slate-50"
                            headerItemClass="font-rocGroteskMedium !text-xs !text-slate-700"
                            tableClass="!border !border-slate-200 cursor-pointer"
                            bodyItemClass="!border-slate-200 !cursor-default hover:!bg-slate-50"
                        />

                        {getNewProductPlanListQuery?.isFetching &&
                            !getNewProductPlanListQuery.isLoading && <Loader />}
                    </div>
                </div>
            </div>
        </ModalContainer>
    );
}
